import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";
import { Truck, Menu, X, LogOut, Bell, User2 } from "lucide-react";
import { Button } from "./ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "./ui/dropdown-menu";

export default function Header({ variant = "marketing" }) {
  const { user, setUser } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const logout = async () => {
    await api.post("/auth/logout");
    setUser(null);
    navigate("/");
  };

  const navLinks = [
    { href: "/#how", label: "Comment ça marche" },
    { href: "/#economies", label: "Économies" },
    { href: "/#matching", label: "Matching A/R" },
    { href: "/avis", label: "Avis clients" },
  ];

  return (
    <header className="fixed top-0 left-0 right-0 z-50 border-b border-slate-200 bg-white/90 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex h-16 items-center justify-between">
        <Link to="/" className="flex items-center gap-2 group" data-testid="brand-logo">
          <span className="flex h-9 w-9 items-center justify-center bg-orange-600 text-white rounded-md shadow-sm group-hover:rotate-[-5deg] transition-transform">
            <Truck className="h-5 w-5" />
          </span>
          <span className="font-display font-extrabold text-lg tracking-tight text-slate-900">
            Depann<span className="text-orange-600">Route</span>
          </span>
        </Link>

        {variant === "marketing" && (
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((l) => {
              const isRoute = l.href.startsWith("/") && !l.href.startsWith("/#");
              if (isRoute) {
                return (
                  <Link key={l.href} to={l.href} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" data-testid={`nav-${l.label}`}>
                    {l.label}
                  </Link>
                );
              }
              return (
                <a key={l.href} href={l.href} className="text-sm font-medium text-slate-600 hover:text-slate-900 transition-colors" data-testid={`nav-${l.label}`}>
                  {l.label}
                </a>
              );
            })}
          </nav>
        )}

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <Link to="/notifications" className="p-2 text-slate-600 hover:text-slate-900" data-testid="header-notifications">
                <Bell className="h-5 w-5" />
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 transition-colors" data-testid="user-menu-trigger">
                    <span className="flex h-7 w-7 items-center justify-center rounded-full bg-slate-900 text-white text-xs font-bold">
                      {user.name?.[0]?.toUpperCase() || "U"}
                    </span>
                    <span className="text-sm font-medium text-slate-900">{user.name}</span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>
                    <div className="text-xs text-slate-500 uppercase tracking-wider">{user.role === "pro" ? "Dépanneur" : user.role === "admin" ? "Admin" : "Client"}</div>
                    <div className="text-sm font-medium text-slate-900">{user.email}</div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate("/dashboard")} data-testid="menu-dashboard">
                    <User2 className="mr-2 h-4 w-4" /> Tableau de bord
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={logout} data-testid="menu-logout">
                    <LogOut className="mr-2 h-4 w-4" /> Déconnexion
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login">
                <Button variant="ghost" className="text-sm font-medium">
                  Connexion
                </Button>
              </Link>
              <Link to="/register" data-testid="nav-register">
                <Button className="bg-orange-600 hover:bg-orange-700 text-white rounded-md">
                  Créer un compte
                </Button>
              </Link>
            </>
          )}
        </div>

        <button className="lg:hidden p-2 text-slate-900" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white px-4 py-4 space-y-3">
          {variant === "marketing" && navLinks.map((l) => {
            const isRoute = l.href.startsWith("/") && !l.href.startsWith("/#");
            if (isRoute) {
              return (
                <Link key={l.href} to={l.href} onClick={() => setOpen(false)} className="block text-sm font-medium text-slate-700">
                  {l.label}
                </Link>
              );
            }
            return (
              <a key={l.href} href={l.href} onClick={() => setOpen(false)} className="block text-sm font-medium text-slate-700">
                {l.label}
              </a>
            );
          })}
          {user ? (
            <>
              <Link to="/dashboard" onClick={() => setOpen(false)} className="block text-sm font-medium text-slate-700">Tableau de bord</Link>
              <button onClick={logout} className="block text-sm font-medium text-red-600">Déconnexion</button>
            </>
          ) : (
            <div className="flex gap-2">
              <Link to="/login" onClick={() => setOpen(false)} className="flex-1"><Button variant="outline" className="w-full">Connexion</Button></Link>
              <Link to="/register" onClick={() => setOpen(false)} className="flex-1"><Button className="w-full bg-orange-600 hover:bg-orange-700">Créer un compte</Button></Link>
            </div>
          )}
        </div>
      )}
    </header>
  );
}
